// ABOUTME: Claude Agent SDK message types for WebSocket streaming.
// ABOUTME: Matches the contracts defined in docs/api/02-frontend-backend-contract.md.

// Main SDK streaming message type - union of all message types
export type SDKStreamingMessage = AssistantMessage | StreamingTextChunk | SystemMessage | ResultMessage | MessageSavedEvent;

// Assistant message containing complete content blocks
export interface AssistantMessage {
  type: 'assistant';
  content: ContentBlock[];
  model?: string | null;
  stop_reason?: string | null;
  usage?: {
    input_tokens: number;
    output_tokens: number;
  } | null;
}

// Streaming text chunk for token-by-token display (simplified format from working example)
export interface StreamingTextChunk {
  type: 'text';
  content: string;  // Just the text chunk
}

// System message for progress and status updates
export interface SystemMessage {
  type: 'system';
  subtype: 'init' | 'progress' | 'error' | 'info' | 'complete';
  message: string;
  phase?: string | null;
  progress?: number | null;
  data?: Record<string, unknown> | null;
}

// Result message indicating completion or error
export interface ResultMessage {
  type: 'result';
  subtype: 'success' | 'error_max_turns' | 'error_during_execution' | 'error_max_budget_usd';
  session_id: string;
  duration_ms: number;
  duration_api_ms: number;
  is_error: boolean;
  num_turns: number;
  total_cost_usd: number;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
  result?: string;
  errors?: string[];
}

// Message saved event - sent after assistant message is persisted to DB
// Contains the real MongoDB ID for linking pins to messages
export interface MessageSavedEvent {
  type: 'message_saved';
  message_id: string;
}

// Content blocks that appear in AssistantMessage
export type ContentBlock = TextBlock | ThinkingBlock | ToolUseBlock | ToolResultBlock | StructuredResponseBlock | ParseErrorBlock;

// Text block for regular text content
export interface TextBlock {
  type: 'text';
  text: string;
}

// Thinking block showing agent's reasoning
export interface ThinkingBlock {
  type: 'thinking';
  thinking: string;
  signature: string;
}

// Tool use block indicating a tool call
export interface ToolUseBlock {
  type: 'tool_use';
  id: string;
  name: string;
  input: Record<string, unknown>;
}

// Tool result block containing tool execution output
export interface ToolResultBlock {
  type: 'tool_result';
  tool_use_id: string;
  content: string | Record<string, unknown>[] | null;
  is_error?: boolean;
}

// =============================================================================
// NEW: Structured Response Types
// =============================================================================

// Structured response block - the new format for Claude's final response
export interface StructuredResponseBlock {
  type: 'structured_response';
  response_type: 'text' | 'insight';
  content: TextResponseContent | InsightResponseContent;
  raw_json: string;
}

// Parse error block - when structured response parsing fails
export interface ParseErrorBlock {
  type: 'parse_error';
  error_message: string;
  raw_content: string;
  details: Record<string, unknown>;
}

// Text response content (for casual/meta questions)
export interface TextResponseContent {
  type: 'text';
  content: string;
}

// Insight response content (for data questions)
export interface InsightResponseContent {
  type: 'insight';
  insight: StructuredInsight;
}

// =============================================================================
// Structured Insight Types
// =============================================================================

export type InsightType = 'metric-card' | 'chart' | 'table' | 'summary' | 'table-summary' | 'chart-table' | 'python-viz' | 'python-viz-with-summary';

export interface StructuredInsight {
  id: string;
  type: InsightType;
  title: string;
  // Single paragraph describing the full insight - what it shows, key findings, etc.
  insight_overview?: string;
  selection_reasoning?: SelectionReasoning;
  chart?: ChartConfig | null;
  table?: TableConfig | null;
  summary?: SummaryConfig | null;
  key_metrics?: KeyMetric[];
  python_viz?: PythonVizConfig | null;
  smart_actions?: SmartAction[];
  generated_at?: string;
}

export interface SelectionReasoning {
  primary_reason: string;
  confidence: 'high' | 'medium' | 'low';
  alternatives_considered?: AlternativeConsidered[];
}

export interface AlternativeConsidered {
  type: string;
  reason: string;
}

// Python Viz configuration
export interface PythonVizConfig {
  viz_type: 'heatmap' | 'sankey' | 'geo_map' | 'radar' | 'gauge' | 'treemap' | 'funnel' | 'waterfall' | 'other';
  plotly_spec: Record<string, unknown>;
  aspect_ratio?: number;
  viz_ref?: string;
}

// Chart configuration
export type ChartType = 'bar' | 'line' | 'area' | 'pie' | 'stacked-bar' | 'grouped-bar';

export interface ChartConfig {
  type: ChartType;
  data: ChartDataPoint[];
  x_axis?: AxisConfig | null;
  y_axis?: AxisConfig | null;
  colors?: string[] | null;
  show_legend?: boolean;
}

export interface ChartDataPoint {
  label: string;
  value: number;
  [key: string]: string | number;
}

export interface AxisConfig {
  label?: string | null;
  format?: 'number' | 'currency' | 'percentage' | 'date' | null;
  currency?: string | null;
}

// Table configuration
export interface TableConfig {
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  max_display_rows?: number;
  show_row_numbers?: boolean;
}

export interface TableColumn {
  id: string;
  header: string;
  type?: 'text' | 'number' | 'currency' | 'percentage' | 'date';
  align?: 'left' | 'center' | 'right' | null;
}

// Summary configuration
export interface SummaryConfig {
  introduction?: string | null;
  bullet_points: BulletPoint[];
  conclusion?: string | null;
}

export interface BulletPoint {
  text: string;
  emphasis?: 'normal' | 'highlight' | 'critical';
  sub_points?: string[];
}

// Key metric (for metric-card type)
export interface KeyMetric {
  label: string;
  value: number;
  display_value: string;
  sentiment: 'positive' | 'negative' | 'neutral' | 'warning' | 'critical';
  sentiment_reason?: string | null;
  change?: MetricChange | null;
  context?: string | null;
}

export interface MetricChange {
  value: number;
  direction: 'up' | 'down' | 'flat';
  sentiment: 'positive' | 'negative' | 'neutral';
  period_label?: string;
}

// Smart action
export type SmartActionIcon = 'drill-down' | 'compare' | 'export' | 'filter' | 'share' | 'alert' | 'view' | 'create' | 'ask' | 'refresh';

export interface SmartAction {
  id: string;
  label: string;
  icon?: SmartActionIcon;
  action_type: 'query' | 'navigate' | 'export';
  query?: string | null;
  export_format?: 'csv' | 'pdf' | 'png' | null;
}

// Progress update data structure (commonly used in SystemMessage)
export interface ProgressData {
  phase: 'schema_discovery' | 'query_planning' | 'query_execution' | 'analysis' | 'visualization';
  message: string;
  progress: number;
}

// Helper type guards for content blocks
export function isTextBlock(block: ContentBlock): block is TextBlock {
  return block.type === 'text';
}

export function isThinkingBlock(block: ContentBlock): block is ThinkingBlock {
  return block.type === 'thinking';
}

export function isToolUseBlock(block: ContentBlock): block is ToolUseBlock {
  return block.type === 'tool_use';
}

export function isToolResultBlock(block: ContentBlock): block is ToolResultBlock {
  return block.type === 'tool_result';
}

export function isStructuredResponseBlock(block: ContentBlock): block is StructuredResponseBlock {
  return block.type === 'structured_response';
}

export function isParseErrorBlock(block: ContentBlock): block is ParseErrorBlock {
  return block.type === 'parse_error';
}

// Helper type guards for message types
export function isAssistantMessage(message: SDKStreamingMessage): message is AssistantMessage {
  return message.type === 'assistant' && Array.isArray((message as any).content);
}

export function isStreamingTextChunk(message: SDKStreamingMessage): message is StreamingTextChunk {
  return message.type === 'text';
}

export function isSystemMessage(message: SDKStreamingMessage): message is SystemMessage {
  return message.type === 'system';
}

export function isResultMessage(message: SDKStreamingMessage): message is ResultMessage {
  return message.type === 'result';
}

export function isMessageSavedEvent(message: SDKStreamingMessage): message is MessageSavedEvent {
  return message.type === 'message_saved';
}

/** Data from a resolve_ambiguity tool result */
export interface ClarificationCardData {
  type: 'clarification_card';
  question: string;
  options: ClarificationOption[];
  category: 'metric' | 'dimension' | 'relationship' | 'filter' | 'time_grain' | 'analytical_intent' | 'methodology';
  persist_as: string;
  recommendation: string | null;
  workspace_id: string;
  investigation_id: string;
  additional_questions?: AdditionalQuestion[];
}

export interface AdditionalQuestion {
  question: string;
  options?: ClarificationOption[];
  category?: string;
}

export interface ClarificationOption {
  id: string;
  label: string;
  description: string;
  sql_preview?: string;
  data_preview?: string;
}
